import styles from "./BaseButton.module.scss";

import { ContentSide, Tooltip } from "@/dls/Tooltip";
import { Wrapper } from "@/dls/Wrapper";
import { Link, LinkProps } from "@/dls/Link";

import { cn } from "@/utils/cn";

type ButtonProps = React.ComponentProps<"button">;
type BaseProps = Omit<ButtonProps, "prefix">;

enum ButtonVariant {
  Primary = "primary",
  Secondary = "secondary",
  Warning = "warning",
  Info = "info",
  Success = "success",
  Danger = "danger",
  Inverse = "inverse",
}

enum ButtonAppearance {
  Filled = "filled",
  Outlined = "outlined",
  Dashed = "dashed",
  Ghost = "ghost",
  Silent = "silent",
}

interface Props extends BaseProps {
  href?: string;
  locale?: LinkProps["locale"];
  prefetch?: LinkProps["prefetch"];
  replace?: LinkProps["replace"];
  scroll?: LinkProps["scroll"];
  shallow?: LinkProps["shallow"];
  tooltip?: React.ReactNode;
  tooltipContentSide?: ContentSide;
  variant?: ButtonVariant;
  appearance?: ButtonAppearance;
  ariaLabel?: string;
  shouldHaveShadow?: boolean;
}

const BaseButton: React.FC<Props> = ({
  href,
  tooltip,
  variant = ButtonVariant.Secondary,
  appearance = ButtonAppearance.Filled,
  tooltipContentSide = ContentSide.BOTTOM,
  shouldHaveShadow,
  ariaLabel,
  className,
  children,
  locale,
  prefetch,
  replace,
  scroll,
  shallow,
  ...rest
}) => {
  const classes = cn(
    styles.base,
    styles[variant],
    styles[appearance],
    className
  );

  const commonProps = {
    className: classes,
    "data-shadow": shouldHaveShadow,
    "aria-label": ariaLabel,
  };

  return (
    <Wrapper
      shouldWrap={!!tooltip}
      wrapper={(tooltipChildren) => (
        <Tooltip text={tooltip} contentSide={tooltipContentSide}>
          {tooltipChildren}
        </Tooltip>
      )}
    >
      {href ? (
        <Link
          href={href}
          locale={locale}
          prefetch={prefetch}
          replace={replace}
          scroll={scroll}
          shallow={shallow}
          {...commonProps}
        >
          {children}
        </Link>
      ) : (
        <button {...commonProps} {...rest}>
          {children}
        </button>
      )}
    </Wrapper>
  );
};

export { BaseButton, ButtonVariant, ButtonAppearance };
